import {Box, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography} from "@mui/material";
import {CompareArrows as CompareIcon, ArrowUpward, ArrowDownward, Remove} from "@mui/icons-material";
import {colors, borders, shadows} from "../../ui/colors";

const DiffCell = ({value, suffix = ""}) => {
    if (value == null || value === 0) {
        return (
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", gap: 0.25}}>
                <Remove sx={{fontSize: 14, color: colors.textMuted}}/>
            </Box>
        );
    }

    const isPositive = value > 0;
    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.25,
                backgroundColor: isPositive ? colors.successBg : colors.errorBg,
                color: isPositive ? colors.successText : colors.errorText,
                padding: "2px 8px",
                borderRadius: borders.radius.full,
                fontSize: "12px",
                fontWeight: 600,
            }}
        >
            {isPositive ? (
                <ArrowUpward sx={{fontSize: 12}}/>
            ) : (
                <ArrowDownward sx={{fontSize: 12}}/>
            )}
            {Math.abs(Math.round(value))}{suffix}
        </Box>
    );
};

const cellSx = {
    fontSize: "13px",
    fontWeight: 500,
    color: colors.textPrimary,
    textAlign: "center",
    padding: "10px 12px",
    borderBottom: borders.default,
};

const headerCellSx = {
    fontSize: "11px",
    fontWeight: 600,
    color: colors.textMuted,
    textAlign: "center",
    padding: "8px 12px",
    backgroundColor: colors.bg,
    borderBottom: borders.default,
    whiteSpace: "nowrap",
};

const ExerciseComparisonTable = ({comparisons, weekFinished = true}) => {
    if (!comparisons || comparisons.length === 0) return null;

    return (
        <Paper
            elevation={0}
            sx={{
                border: borders.default,
                borderRadius: borders.radius.lg,
                boxShadow: shadows.card,
                overflow: "hidden",
                direction: "rtl",
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    padding: {xs: "12px 16px", md: "20px 24px"},
                    borderBottom: borders.default,
                }}
            >
                <CompareIcon sx={{color: colors.textSecondary}}/>
                <Typography sx={{fontSize: "18px", fontWeight: 600, color: colors.textPrimary}}>
                    {weekFinished ? "השוואת תרגילים לשבוע קודם" : "תרגילים שבוצעו עד כה"}
                </Typography>
            </Box>

            <Box sx={{overflowX: "auto"}}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{...headerCellSx, textAlign: "right"}}>תרגיל</TableCell>
                            <TableCell sx={headerCellSx}>סטים</TableCell>
                            <TableCell sx={headerCellSx}>חזרות</TableCell>
                            <TableCell sx={headerCellSx}>נפח (ק״ג)</TableCell>
                            {weekFinished && <TableCell sx={headerCellSx}>שינוי סטים</TableCell>}
                            {weekFinished && <TableCell sx={headerCellSx}>שינוי חזרות</TableCell>}
                            {weekFinished && <TableCell sx={headerCellSx}>שינוי נפח</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {comparisons.map((ex, idx) => (
                            <TableRow
                                key={idx}
                                sx={{
                                    "&:hover": {backgroundColor: colors.bg},
                                    "&:last-child td": {borderBottom: "none"},
                                }}
                            >
                                <TableCell sx={{...cellSx, textAlign: "right", fontWeight: 600}}>
                                    {ex.exerciseNameHeb || ex.exerciseName}
                                </TableCell>
                                <TableCell sx={cellSx}>{ex.totalSets ?? 0}</TableCell>
                                <TableCell sx={cellSx}>{ex.totalReps ?? 0}</TableCell>
                                <TableCell sx={cellSx}>
                                    {ex.totalVolume != null ? Math.round(ex.totalVolume).toLocaleString() : 0}
                                </TableCell>
                                {weekFinished && (
                                    <TableCell sx={{...cellSx, padding: "6px 8px"}}>
                                        <DiffCell value={ex.setsDiff}/>
                                    </TableCell>
                                )}
                                {weekFinished && (
                                    <TableCell sx={{...cellSx, padding: "6px 8px"}}>
                                        <DiffCell value={ex.repsDiff}/>
                                    </TableCell>
                                )}
                                {weekFinished && (
                                    <TableCell sx={{...cellSx, padding: "6px 8px"}}>
                                        <DiffCell value={ex.volumeDiff}/>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        </Paper>
    );
};

export default ExerciseComparisonTable;
